import {
  compact,
  find,
  get,
  head,
  isEmpty,
  keys,
  map,
  orderBy,
  partition,
  range,
  reduce,
  values
} from "lodash";
import moment from "moment";
import { nanoid } from "nanoid";
import { parse } from "query-string";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { matchPath, useLocation } from "react-router-dom";
import { isActiveInstall } from "../../pages/Workspace/InstallsData/utils";
import {
  geoToH3Memoized,
  h3GetResolutionMemoized,
  h3ToChildrenMemoized,
  h3ToGeoMemoized,
  h3ToParentMemoized,
  isActiveTransmitScaleMemoized,
  kRingMemoized
} from "../../utils/memoizedMethods";
import { Hotspot, HotspotLocation } from "../../utils/types";
import useMapConfig, { VisualizationLayer } from "../../utils/useMapConfig";
import usePrevious from "../../utils/usePrevious";
import useTransmitScaleConfig from "../../utils/useTransmitScaleConfig";
import { useHeliumData } from "../heliumData";
import { useHotspots } from "../hotspots";
import { useInstalls } from "../installs";
import { useLocations } from "../locations";
import { DEFAULT_HEIGHT_IN_METERS } from "../locations/LocationFormSidebar";
import { useStats } from "../stats";

interface Hip17ChainVars {
  numberOfSiblings: number;
  densityTarget: number;
  densityMax: number;
}

export interface Hip17ChainVarsByResolution {
  [resolution: number]: Hip17ChainVars;
}

export interface HexTransmitScaleInfo {
  h3Index: string;
  resolution: number;
  unclippedDensity: number;
  clippedDensity: number;
  transmitScale: number;
  limit: number;
  hip17ChainVars: Hip17ChainVars;
  densityStatusMessage: string;
  excess: number;
  shortOfDensityTarget: number;
  limitCanBeIncreased: boolean;
  occupiedCount: number;
}

interface TransmitScaleWithInfoAndProblems {
  transmitScale: number;
  parentHexes: {
    [resolution: number]: string; // h3Index
  };
  problematicResolutions: number[];
}

type ContextValue = {
  calculateHexTransmitScale: (h3Index: string) => number;
  isInteractiveHotspot: (lastPocChallenge: number) => boolean;
  calculatedTransmitScales: { [hotspotId: string]: number };
  problematicHexes: string[];
  getTransmitScaleInfo: (
    h3IndexRes12: string
  ) => TransmitScaleWithInfoAndProblems;
  isInteractiveHotspotEnhanced: (
    hotspotId: string,
    lastPocChallenge?: number
  ) => boolean;
  getHexTransmitScaleInfo: (h3Index: string) => HexTransmitScaleInfo;
  generateLocationsToReachTargetDensity: (
    h3Index: string,
    renderLocationName: (locationNumber: number) => string
  ) => HotspotLocation[];
  isTransmitScaleActive: (id: string) => boolean;
  simulatedDisabledIds: string[];
  setSimulatedDisabledIds: React.Dispatch<React.SetStateAction<string[]>>;
  isHip17Experiment: boolean;
  getLocationWitnessRewardUnits: (
    location: HotspotLocation
  ) => number | undefined;
};

const TransmitScalesContext = React.createContext<ContextValue | undefined>(
  undefined
);

interface Props {}

export const TransmitScalesProvider: React.FC<Props> = (props) => {
  const { chainVars } = useHeliumData();
  const { stats } = useStats();
  const location = useLocation();
  const { hotspotsAroundCenter, getHotspotCoordinates } = useHotspots(); // TODO: do the same for locations
  const { installs } = useInstalls();
  const {
    locations,
    locationsAroundCenter,
    getLocationCoordinates,
    simulatedLocations,
    setSimulatedLocations
  } = useLocations();
  const disabledIds = useTransmitScaleConfig((state) => state.disabledIds);
  const forcedInteractiveIds = useTransmitScaleConfig(
    (state) => state.forcedInteractiveIds
  );
  const [simulatedDisabledIds, setSimulatedDisabledIds] = useState<string[]>(
    []
  );
  const pointColorMetricIteratee = useMapConfig(
    (state) => state.pointColorMetricIteratee
  );
  const visibleLayers = useMapConfig((state) => state.visibleLayers);

  const isHip17Experiment = useMemo(() => {
    const queryParams = parse(location.search);
    return queryParams.experiment === "hip17";
  }, [location.search]);

  const hexDetailsPageMatch = matchPath<{
    tab?: string;
    h3Index?: string;
    section?: string;
    subsection?: string;
  }>(location.pathname, {
    path: ["/hexes/:h3Index/:section/:subsection", "/hexes/:h3Index/:section"]
  });

  const hotspotDetailsPageMatch = matchPath<{
    id: string;
    section: string;
    subsection?: string;
  }>(location.pathname, {
    path: ["/hotspots/:id/:section/:subsection", "/hotspots/:id/:section"]
  });

  const locationDetailsPageMatch = matchPath<{
    locationUuid: string;
    section?: string;
    simulationUuid?: string;
  }>(location.pathname, {
    path: [
      "/locations/:locationUuid/:section",
      "/locations/:locationUuid/super-simulations/:simulationUuid/:subsection"
    ]
  });

  const prevH3Index = usePrevious(hexDetailsPageMatch?.params.h3Index);
  useEffect(() => {
    if (hexDetailsPageMatch?.params.h3Index !== prevH3Index) {
      setSimulatedLocations([]);
      setSimulatedDisabledIds([]);
    }
  }, [hexDetailsPageMatch?.params.h3Index, prevH3Index, setSimulatedLocations]);

  const isTransmitScaleActive = useCallback(
    (id: string): boolean => {
      if (find(simulatedLocations, { uuid: id })) {
        isActiveTransmitScaleMemoized(id, true);

        return true;
      }

      const isLocationWithActiveInstalls =
        !!locations[id] &&
        !!find(values(installs).filter(isActiveInstall), { location_uuid: id });

      if (isLocationWithActiveInstalls) {
        isActiveTransmitScaleMemoized(id, false);

        return false;
      }

      const excludeFromDisabledIds = !simulatedDisabledIds
        .concat(disabledIds)
        .includes(id);

      isActiveTransmitScaleMemoized(id, excludeFromDisabledIds);

      return excludeFromDisabledIds;
    },
    [disabledIds, installs, locations, simulatedDisabledIds, simulatedLocations]
  );

  const explorerPageMatch = matchPath<{
    tab?: string; // hotspots, locations, hexes, contacts, installs
  }>(location.pathname, {
    path: "/:tab"
  });

  const activeLocationsAroundCenter = useMemo(
    () =>
      locationsAroundCenter.filter((location) =>
        isTransmitScaleActive(location.uuid)
      ),
    [isTransmitScaleActive, locationsAroundCenter]
  );

  const activeLocationH3Indices = useMemo(
    () =>
      compact(
        activeLocationsAroundCenter.map(
          (location) => getLocationCoordinates(location.uuid)?.h3Index
        )
      ),
    [activeLocationsAroundCenter, getLocationCoordinates]
  );

  const visibleActiveLocationsH3Indices = useMemo(() => {
    if (
      !visibleLayers[VisualizationLayer.locations] &&
      explorerPageMatch?.params.tab !== "locations"
    ) {
      return [];
    }

    return activeLocationH3Indices;
  }, [activeLocationH3Indices, explorerPageMatch?.params.tab, visibleLayers]);

  const disabledHotspotIdsAroundCenter = useMemo(
    () =>
      hotspotsAroundCenter.filter(
        (hotspot) => !isTransmitScaleActive(hotspot.id)
      ),
    [hotspotsAroundCenter, isTransmitScaleActive]
  );

  const omitTransmitScaleCalculations = useMemo(() => {
    if (!isEmpty(visibleActiveLocationsH3Indices)) {
      return false;
    }

    if (
      locationDetailsPageMatch?.params?.section &&
      ["reward-scaling", "super-simulations"].includes(
        locationDetailsPageMatch.params.section
      )
    ) {
      return false;
    }

    return (
      isEmpty(disabledHotspotIdsAroundCenter) &&
      pointColorMetricIteratee !== "reward_scale" &&
      location.pathname !== "/hexes" &&
      !hexDetailsPageMatch?.params &&
      hotspotDetailsPageMatch?.params.section !== "reward-scaling"
    );
  }, [
    disabledHotspotIdsAroundCenter,
    hexDetailsPageMatch?.params,
    hotspotDetailsPageMatch?.params.section,
    location.pathname,
    locationDetailsPageMatch?.params.section,
    pointColorMetricIteratee,
    visibleActiveLocationsH3Indices
  ]);

  const minBlockHeightForInteractivity = useMemo(
    () =>
      !chainVars?.hip17InteractivityBlocks || !stats?.blocks
        ? undefined
        : stats.blocks - chainVars.hip17InteractivityBlocks,
    [chainVars?.hip17InteractivityBlocks, stats?.blocks]
  );

  const isInteractiveHotspot = useCallback(
    (lastPocChallenge?: number) =>
      calculateIsInteractiveHotspot(
        lastPocChallenge,
        minBlockHeightForInteractivity
      ),
    [minBlockHeightForInteractivity]
  );

  const isInteractiveHotspotEnhanced = useCallback(
    (hotspotId: string, lastPocChallenge?: number) => {
      if (!isTransmitScaleActive(hotspotId)) return false;

      if (forcedInteractiveIds.includes(hotspotId)) return true;

      return isInteractiveHotspot(lastPocChallenge);
    },
    [forcedInteractiveIds, isInteractiveHotspot, isTransmitScaleActive]
  );

  // TODO: this only works for the neighboringHotspots, not for the ownerHotspots. So basically the app should only calculate the ones in sight of the map. The others, such as in the overview page, should just display the database values.
  // So when the owner label is filtered, it should just display the database value.
  // TODO: fetch interactive hotspots based on the kRing(hex, 2) at the lowest resolution, similar to the source code
  // TODO: ensure that non-interactive hotspots get a transmit scale of 0. More info: https://github.com/helium/HIP/blob/master/0017-hex-density-based-transmit-reward-scaling.md#algorithm
  const interactiveHotspotIds = useMemo(
    () =>
      omitTransmitScaleCalculations
        ? []
        : map(
            hotspotsAroundCenter.filter((hotspot) =>
              isInteractiveHotspotEnhanced(
                hotspot.id,
                hotspot.last_poc_challenge
              )
            ),
            "id"
          ),
    [
      hotspotsAroundCenter,
      isInteractiveHotspotEnhanced,
      omitTransmitScaleCalculations
    ]
  );

  const interactiveHotspotLocations = useMemo(
    () =>
      omitTransmitScaleCalculations
        ? []
        : compact(
            interactiveHotspotIds.map(
              (hotspotId) => getHotspotCoordinates(hotspotId)?.h3Index
            )
          ),
    [
      getHotspotCoordinates,
      interactiveHotspotIds,
      omitTransmitScaleCalculations
    ]
  );

  const resolutions = useMemo(() => {
    if (
      !chainVars?.densityTgtRes ||
      isEmpty(interactiveHotspotLocations) ||
      omitTransmitScaleCalculations
    ) {
      return [];
    }

    let innerMostResolution = Math.max.apply(
      null,
      interactiveHotspotLocations.map(h3GetResolutionMemoized)
    );

    const outerMostParentResolution = chainVars.densityTgtRes;

    if (isHip17Experiment) {
      innerMostResolution = 9;
    }

    return range(outerMostParentResolution, innerMostResolution + 1).reverse();
  }, [
    isHip17Experiment,
    chainVars?.densityTgtRes,
    interactiveHotspotLocations,
    omitTransmitScaleCalculations
  ]);

  const hip17ChainVarsByResolution = useMemo(() => {
    if (!chainVars) return undefined;

    const chainVarsByResolution = resolutions.reduce(
      (acc: Hip17ChainVarsByResolution, resolution) => {
        const [numberOfSiblings, densityTarget, densityMax] = get(
          chainVars,
          `hip17Res${resolution}`
        ) as [number, number, number];
        acc[resolution] = { numberOfSiblings, densityTarget, densityMax };
        return acc;
      },
      {}
    );

    return chainVarsByResolution;
  }, [chainVars, resolutions]);

  React.useEffect(() => {
    if (isHip17Experiment) {
      console.log({ hip17ChainVarsByResolution });
    }
  }, [isHip17Experiment, hip17ChainVarsByResolution]);

  const buildDensities = useCallback(
    (
      childrenH3Indices: string[],
      unclippedDensitiesAcc: DensityMap,
      clippedDensitiesAcc: DensityMap,
      resolutionsAcc: number[]
    ): {
      clippedDensities: DensityMap;
      unclippedDensities: DensityMap;
    } => {
      if (!hip17ChainVarsByResolution || isEmpty(resolutionsAcc))
        return {
          clippedDensities: clippedDensitiesAcc,
          unclippedDensities: unclippedDensitiesAcc
        };

      const [resolution, ...parentResolutions] = resolutionsAcc;

      const { numberOfSiblings, densityTarget, densityMax } =
        hip17ChainVarsByResolution[resolution];

      const unclippedDensitiesAtResolution = getUnclippedDensities(
        childrenH3Indices,
        resolution,
        clippedDensitiesAcc
      );

      const mergedUnclippedDensities = {
        ...unclippedDensitiesAcc,
        ...unclippedDensitiesAtResolution
      };

      const mergedClippedDensities = {
        ...clippedDensitiesAcc,
        ...unclippedDensitiesAtResolution
      };

      const occupiedHexesAtResolution = keys(unclippedDensitiesAtResolution);

      const updatedMergedClippedDensities = occupiedHexesAtResolution.reduce(
        (acc: DensityMap, h3IndexAtResolution) => {
          const occupiedCount = getOccupiedCount(
            densityTarget,
            h3IndexAtResolution,
            unclippedDensitiesAtResolution
          );

          const hexDensityLimit = getHexDensityLimit(
            numberOfSiblings,
            densityTarget,
            densityMax,
            occupiedCount
          );

          acc[h3IndexAtResolution] = Math.min(
            hexDensityLimit,
            mergedClippedDensities[h3IndexAtResolution]
          );

          return acc;
        },
        mergedClippedDensities
      );

      return buildDensities(
        occupiedHexesAtResolution,
        mergedUnclippedDensities,
        updatedMergedClippedDensities,
        parentResolutions
      );
    },
    [hip17ChainVarsByResolution]
  );

  const { innerMostLocationHexes, initialDensities } = useMemo(() => {
    if (omitTransmitScaleCalculations) {
      return { innerMostLocationHexes: [], initialDensities: {} };
    }

    return [
      ...simulatedLocations.map((location) => location.h3_index_r12),
      ...activeLocationH3Indices,
      ...interactiveHotspotLocations
    ].reduce<{
      innerMostLocationHexes: string[];
      initialDensities: DensityMap;
    }>(
      (acc, location: string) => {
        const parentHex = h3ToParentMemoized(location, resolutions[0]);

        if (!acc.initialDensities[parentHex]) {
          acc.initialDensities[parentHex] = 0;
          acc.innerMostLocationHexes.push(parentHex);
        }

        acc.initialDensities[parentHex]++;

        return acc;
      },
      {
        innerMostLocationHexes: [],
        initialDensities: {}
      }
    );
  }, [
    activeLocationH3Indices,
    interactiveHotspotLocations,
    omitTransmitScaleCalculations,
    resolutions,
    simulatedLocations
  ]);

  const { clippedDensities, unclippedDensities } = useMemo(() => {
    if (omitTransmitScaleCalculations) {
      return { clippedDensities: {}, unclippedDensities: {} };
    }

    const [, ...remainingResolutions] = resolutions;

    return buildDensities(
      innerMostLocationHexes,
      initialDensities,
      initialDensities,
      remainingResolutions
    );
  }, [
    buildDensities,
    initialDensities,
    innerMostLocationHexes,
    omitTransmitScaleCalculations,
    resolutions
  ]);

  const calculateHexTransmitScale = useCallback(
    (h3Index: string): number =>
      reduce(
        resolutions,
        (acc: number, resolution) => {
          const parentH3Index = h3ToParentMemoized(h3Index, resolution);
          const unclippedDensity = unclippedDensities[parentH3Index];
          return unclippedDensity
            ? acc * (clippedDensities[parentH3Index] / unclippedDensity)
            : acc;
        },
        1
      ),
    [clippedDensities, resolutions, unclippedDensities]
  );

  const getTransmitScaleInfo = useCallback(
    (h3Index: string): TransmitScaleWithInfoAndProblems => {
      const parentHexes: { [resolution: number]: string } = {};

      const problematicResolutions: number[] = [];

      const transmitScale = resolutions.reduce((acc, resolution) => {
        const parentH3Index = h3ToParentMemoized(h3Index, resolution);
        const unclippedDensity = unclippedDensities[parentH3Index];
        const clippedDensity = clippedDensities[parentH3Index];
        const transmitScaleAcc = unclippedDensity
          ? acc * (clippedDensity / unclippedDensity)
          : acc;

        if (transmitScaleAcc < acc) {
          problematicResolutions.push(resolution);
        }

        parentHexes[resolution] = parentH3Index;

        return transmitScaleAcc;
      }, 1);

      return {
        transmitScale,
        parentHexes,
        problematicResolutions
      };
    },
    [clippedDensities, resolutions, unclippedDensities]
  );

  const getHotspotTransmitScaleInfo = useCallback(
    (hotspot: Hotspot) => {
      const hotspotCoordinates = getHotspotCoordinates(hotspot.id);

      if (
        !hotspotCoordinates ||
        !isInteractiveHotspotEnhanced(hotspot.id, hotspot.last_poc_challenge)
      )
        return {
          transmitScale: 0,
          parentHexes: {},
          problematicResolutions: []
        };

      return getTransmitScaleInfo(hotspotCoordinates.h3Index);
    },
    [getHotspotCoordinates, getTransmitScaleInfo, isInteractiveHotspotEnhanced]
  );

  const getHexTransmitScaleInfo = useCallback(
    (h3Index: string) => {
      // This can be at any resolution. Meant for the hex details page
      const resolution = h3GetResolutionMemoized(h3Index);
      const hip17ChainVars = get(hip17ChainVarsByResolution, resolution, {
        numberOfSiblings: 0,
        densityTarget: 0,
        densityMax: 0
      });
      const clippedDensity = get(clippedDensities, h3Index, 0);
      const unclippedDensity = get(unclippedDensities, h3Index, 0);
      const occupiedCount = getOccupiedCount(
        hip17ChainVars.densityTarget,
        h3Index,
        unclippedDensities
      );
      const limit = getHexDensityLimit(
        hip17ChainVars.numberOfSiblings,
        hip17ChainVars.densityTarget,
        hip17ChainVars.densityMax,
        occupiedCount
      );

      let densityStatusMessage = "";
      const excess = unclippedDensity - limit;
      if (excess > 0) {
        densityStatusMessage = `${excess} too many`;
      } else if (excess < 0) {
        densityStatusMessage = `${-excess} available`;
      } else {
        densityStatusMessage = "Fully occupied";
      }

      return {
        h3Index,
        resolution,
        unclippedDensity,
        clippedDensity,
        transmitScale: unclippedDensity ? clippedDensity / unclippedDensity : 1,
        limit,
        hip17ChainVars,
        densityStatusMessage,
        excess,
        shortOfDensityTarget: Math.max(
          hip17ChainVars.densityTarget - unclippedDensity,
          0
        ),
        limitCanBeIncreased: limit < hip17ChainVars.densityMax,
        occupiedCount // TODO: make this the number of neighbors that need to reach the target in order to increase the limit
      };
    },
    [clippedDensities, hip17ChainVarsByResolution, unclippedDensities]
  );

  const hotspotsRewardsAndProblems = useMemo(
    () =>
      hotspotsAroundCenter.reduce<{
        transmitScale: { [hotspotOrLocationId: string]: number };
        problemHexes: string[];
      }>(
        (acc, hotspot) => {
          if (omitTransmitScaleCalculations) {
            acc.transmitScale[hotspot.id] = hotspot.reward_scale || 0;
            return acc;
          }

          const transmitScaleInfoByResolution =
            getHotspotTransmitScaleInfo(hotspot);

          transmitScaleInfoByResolution.problematicResolutions.forEach(
            (resolution) => {
              const h3Index =
                transmitScaleInfoByResolution.parentHexes[resolution];
              if (!acc.problemHexes.includes(h3Index)) {
                acc.problemHexes.push(h3Index);
              }
            }
          );

          acc.transmitScale[hotspot.id] =
            transmitScaleInfoByResolution.transmitScale;

          return acc;
        },
        {
          transmitScale: {},
          problemHexes: []
        }
      ),
    [
      getHotspotTransmitScaleInfo,
      hotspotsAroundCenter,
      omitTransmitScaleCalculations
    ]
  );

  const [calculatedTransmitScales, problematicHexes] = useMemo(() => {
    const { transmitScale, problemHexes } = activeLocationsAroundCenter.reduce(
      (acc, location) => {
        if (omitTransmitScaleCalculations) {
          acc.transmitScale[location.uuid] = 1;
          return acc;
        }

        let locationH3Index = location.h3_index_r12;

        if (!find(simulatedLocations, { uuid: location.uuid })) {
          const coordinates = getLocationCoordinates(location.uuid);
          if (!coordinates) return acc;

          locationH3Index = coordinates.h3Index;
        }

        const transmitScaleInfoByResolution =
          getTransmitScaleInfo(locationH3Index);

        transmitScaleInfoByResolution.problematicResolutions.forEach(
          (resolution) => {
            const h3Index =
              transmitScaleInfoByResolution.parentHexes[resolution];
            if (!acc.problemHexes.includes(h3Index)) {
              acc.problemHexes.push(h3Index);
            }
          }
        );

        acc.transmitScale[location.uuid] =
          transmitScaleInfoByResolution.transmitScale;

        return acc;
      },
      hotspotsRewardsAndProblems
    );
    return [transmitScale, problemHexes];
  }, [
    activeLocationsAroundCenter,
    getLocationCoordinates,
    getTransmitScaleInfo,
    hotspotsRewardsAndProblems,
    omitTransmitScaleCalculations,
    simulatedLocations
  ]);

  const generateLocationsToReachTargetDensity = useCallback(
    (
      h3Index: string,
      renderLocationName: (locationNumber: number) => string
    ) => {
      const { shortOfDensityTarget, resolution } =
        getHexTransmitScaleInfo(h3Index);

      let currentResolution = resolution;

      const maximumResolution = Math.max.apply(null, resolutions);

      let incompleteChildren: string[] = [h3Index];

      while (
        currentResolution <= maximumResolution &&
        (incompleteChildren.length < shortOfDensityTarget ||
          currentResolution === resolution) // This is done to ensure that always one granularity lower is used so that the location isn't exactly in the center becuase that overlapped with the neighbor hex letters
      ) {
        currentResolution++;

        let newIncompleteChildren: string[] = [];

        // TODO: this should be extended to yield hexes that don't have any locations or hotspots but need some
        // TODO: it should also take into consideration that adding locations can cause any parent hex to exceed its limit, which should ideally be prevented
        // As a test case, try executing strategy 2 here: http://localhost:3000/hexes/891fa41b4a7ffff/transmit-scale/neighbor-hexes
        // TODO: if there are locations in a hex already, they can be enabled for transmit scaling instead of adding a new one
        for (let i = 0; i < incompleteChildren.length; i++) {
          const incompleteHex = incompleteChildren[i];

          const children = orderBy(
            h3ToChildrenMemoized(incompleteHex, currentResolution),
            (childH3Index) =>
              getHexTransmitScaleInfo(childH3Index).shortOfDensityTarget,
            "desc"
          ).slice(
            0,
            getHexTransmitScaleInfo(incompleteHex).shortOfDensityTarget
          );

          newIncompleteChildren = newIncompleteChildren.concat(children);
        }

        incompleteChildren = newIncompleteChildren;
      }

      const locationsToAdd: HotspotLocation[] = [];

      const getOccupiedHexPartition = (
        h3Indices: string[]
      ): [string[], string[]] =>
        partition(h3Indices, (childHex) =>
          isEmpty(
            activeLocationH3Indices
              .concat(interactiveHotspotLocations)
              .filter(
                (h3Index) =>
                  h3ToParentMemoized(h3Index, currentResolution) === childHex
              )
          )
        );

      const [emptyIncompleteChildren, occupiedIncompleteChildren] =
        getOccupiedHexPartition(incompleteChildren);

      const addLocationToHex = (h3Index: string) => {
        if (locationsToAdd.length < shortOfDensityTarget) {
          const hexToAddLocation = h3Index;

          const [lat, lng] = h3ToGeoMemoized(hexToAddLocation);
          const centerH3R12 = geoToH3Memoized(lat, lng, 12);
          const centerH3R4 = h3ToParentMemoized(centerH3R12, 4);

          locationsToAdd.push({
            uuid: nanoid(),
            name: renderLocationName(locationsToAdd.length + 1),
            lat,
            lng,
            height: DEFAULT_HEIGHT_IN_METERS,
            h3_index_r12: centerH3R12,
            h3_index_r4: centerH3R4,
            created_at: new Date().toISOString(),
            created_by: "",
            updated_at: new Date().toISOString(),
            updated_by: "",
            linked_contact_ids: [],
            metadata: null,
            address: null,
            super_simulations: [],
            comments: []
          });
        }
      };

      emptyIncompleteChildren.reverse().forEach(addLocationToHex);

      if (
        locationsToAdd.length < shortOfDensityTarget &&
        !isEmpty(occupiedIncompleteChildren)
      ) {
        // Iterate over these and go down to the children until one is found without any hotspot or location
        for (let i = 0; i < occupiedIncompleteChildren.length; i++) {
          const occupiedHex = occupiedIncompleteChildren[i];

          currentResolution = h3GetResolutionMemoized(occupiedHex);

          let childrenToIterate: string[] = [occupiedHex];

          while (
            currentResolution <= maximumResolution &&
            !isEmpty(childrenToIterate)
          ) {
            currentResolution++;

            let newChildren: string[] = [];

            for (let j = 0; j < childrenToIterate.length; j++) {
              const h3Index = childrenToIterate[j];

              const children = orderBy(
                h3ToChildrenMemoized(h3Index, currentResolution),
                (childH3Index) =>
                  getHexTransmitScaleInfo(childH3Index).shortOfDensityTarget,
                "desc"
              ).slice(0, getHexTransmitScaleInfo(h3Index).shortOfDensityTarget);

              newChildren = newChildren.concat(children);
            }

            const [emptyNewChildren, occupiedNewChildren] =
              getOccupiedHexPartition(newChildren);

            if (!isEmpty(emptyNewChildren)) {
              addLocationToHex(head(emptyNewChildren) as string);
              childrenToIterate = [];
            } else {
              childrenToIterate = occupiedNewChildren;
            }
          }
        }
      }

      return locationsToAdd;
    },
    [
      activeLocationH3Indices,
      getHexTransmitScaleInfo,
      interactiveHotspotLocations,
      resolutions
    ]
  );

  const getLocationWitnessRewardUnits = useCallback(
    (location: HotspotLocation): number | undefined => {
      const lastSuperSimulation = head(
        orderBy(
          location.super_simulations,
          [(superSimulation) => moment(superSimulation.created_at).valueOf()],
          ["desc"]
        )
      );

      if (!chainVars || !lastSuperSimulation?.simulation_report) return;

      const visibleLocations: string[] =
        lastSuperSimulation.simulation_report.vl;
      const activeVisibleLocations = visibleLocations.filter((uuid) =>
        isTransmitScaleActive(uuid)
      );
      const visibleHotspots: string[] =
        lastSuperSimulation.simulation_report.vh;

      const activeVisibleTargets =
        activeVisibleLocations.concat(visibleHotspots);

      let witnessRewardUnits = 1;

      const pocPerHopMaxWitnesses = get(chainVars, "pocPerHopMaxWitnesses", 14);
      const witnessRedundancy = get(chainVars, "witnessRedundancy", 4);
      const pocRewardDecayRate = get(chainVars, "pocRewardDecayRate", 0.8);

      const cappedWitnesses = Math.min(
        pocPerHopMaxWitnesses,
        activeVisibleTargets.length
      );

      if (cappedWitnesses <= witnessRedundancy) {
        witnessRewardUnits = 1;
      } else {
        witnessRewardUnits =
          (witnessRedundancy -
            (1 - pocRewardDecayRate ** (cappedWitnesses - witnessRedundancy))) /
          cappedWitnesses;
      }

      return witnessRewardUnits;
    },
    [chainVars, isTransmitScaleActive]
  );

  const value = useMemo(
    () => ({
      calculateHexTransmitScale,
      isInteractiveHotspot,
      calculatedTransmitScales,
      problematicHexes,
      getTransmitScaleInfo,
      isInteractiveHotspotEnhanced,
      getHexTransmitScaleInfo,
      generateLocationsToReachTargetDensity,
      isTransmitScaleActive,
      simulatedDisabledIds,
      setSimulatedDisabledIds,
      isHip17Experiment,
      getLocationWitnessRewardUnits
    }),
    [
      calculateHexTransmitScale,
      isInteractiveHotspot,
      calculatedTransmitScales,
      problematicHexes,
      getTransmitScaleInfo,
      isInteractiveHotspotEnhanced,
      getHexTransmitScaleInfo,
      generateLocationsToReachTargetDensity,
      isTransmitScaleActive,
      simulatedDisabledIds,
      setSimulatedDisabledIds,
      isHip17Experiment,
      getLocationWitnessRewardUnits
    ]
  );

  return <TransmitScalesContext.Provider value={value} {...props} />;
};

export const useTransmitScales = (): ContextValue => {
  const context = useContext(TransmitScalesContext);
  if (context === undefined) {
    throw new Error(
      "useTransmitScales must be used within an TransmitScalesProvider"
    );
  }
  return context;
};

//
// Utils
//

interface DensityMap {
  [h3Index: string]: number;
}

const getOccupiedCount = (
  densityTarget: number,
  h3Index: string,
  unclippedDensityMap: DensityMap
) => {
  const neighbors = kRingMemoized(h3Index, 1); // This includes h3Index as well
  return neighbors.reduce(
    (acc: number, neighbor: string) =>
      get(unclippedDensityMap, neighbor, 0) >= densityTarget ? acc + 1 : acc,
    0
  );
};

export const getHexDensityLimit = (
  numberOfSiblings: number,
  densityTarget: number,
  densityMax: number,
  occupiedCount: number
) =>
  Math.min(
    densityMax,
    densityTarget * Math.max(occupiedCount - numberOfSiblings + 1, 1)
  );

const getUnclippedDensities = (
  childrenH3Indices: string[],
  resolution: number,
  clippedDensitiesAcc: DensityMap
) => {
  return childrenH3Indices.reduce<DensityMap>((acc, childH3Index) => {
    const parentHex = h3ToParentMemoized(childH3Index, resolution);

    acc[parentHex] =
      (acc[parentHex] ?? 0) + (clippedDensitiesAcc[childH3Index] ?? 0);

    return acc;
  }, {});
};

export const calculateIsInteractiveHotspot = (
  lastPocChallenge?: number,
  minBlockHeightForInteractivity?: number
) => {
  if (!minBlockHeightForInteractivity) return true; // TODO: this is incorrect.

  return lastPocChallenge !== undefined
    ? lastPocChallenge >= minBlockHeightForInteractivity
    : false;
};
